import { useState, useEffect } from "react";
import { servicesDataEn, servicesDataAr } from "../data/servicesData";
import { useLanguage } from "../LanguageContext";
import LogoBlack from "../assets/logo/Logo_Black.svg";
import LogoWhite from "../assets/logo/Logo_White.svg";

export default function Navbar({ navigate, currentView }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { lang, toggleLang, isAr } = useLanguage();

  const servicesData = isAr ? servicesDataAr : servicesDataEn;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isAtTop = !scrolled && currentView === "home";

  const navLinkClass = `font-medium text-sm tracking-wide transition-all duration-300 ${
    isAtTop ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-primary-600"
  }`;

  const go = (view) => {
    navigate(view);
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  };

  const t = (en, ar) => (isAr ? ar : en);

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          isAtTop
            ? "bg-transparent"
            : "bg-white/60 backdrop-blur-2xl shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <div
            className="cursor-pointer flex items-center group"
            onClick={() => go("home")}
          >
            <img
              src={isAtTop ? LogoWhite : LogoBlack}
              alt="Axion Railway"
              className="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            {["home", "services", "projects", "about"].map((v) => {
              if (v === "services") {
                return (
                  <div
                    key={v}
                    className="relative group"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      onClick={(e) => { e.preventDefault(); go(v); }}
                      className={`${navLinkClass} flex items-center gap-1`}
                    >
                      {t("SERVICES", "الخدمات")}
                      <i className={`ph ph-caret-down transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown */}
                    <div
                      className={`absolute top-full -left-4 mt-4 w-72 bg-white rounded-xl shadow-xl border border-neutral-100 overflow-hidden transition-all duration-300 transform origin-top ${
                        servicesOpen ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"
                      }`}
                    >
                      <div className="py-2">
                        {Object.entries(servicesData).map(([key, service]) => (
                          <button
                            key={key}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate("service-detail", key);
                              setServicesOpen(false);
                              setMobileOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center gap-3 transition-colors group/item"
                          >
                            <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center group-hover/item:bg-primary-600 group-hover/item:text-white transition-colors shrink-0">
                              <i className={`ph ${service.icon} text-xl`} />
                            </div>
                            <span className="text-sm font-semibold text-neutral-800 group-hover/item:text-primary-600">
                              {service.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <a
                  key={v}
                  href="#"
                  onClick={(e) => { e.preventDefault(); go(v); }}
                  className={navLinkClass}
                >
                  {v === "home"
                    ? t("HOME", "الرئيسية")
                    : v === "about"
                    ? t("ABOUT US", "من نحن")
                    : t("PROJECTS", "المشاريع")}
                </a>
              );
            })}

            <div className="h-6 w-px bg-white/20" />

            <button
              onClick={toggleLang}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 border ${
                isAtTop
                  ? "text-white border-white/30 hover:bg-white/20 bg-white/10 backdrop-blur-sm"
                  : "text-gray-700 border-gray-300 hover:border-primary-500 hover:text-primary-600 bg-white/60"
              }`}
            >
              <i className="ph ph-globe text-base" />
              <span>{isAr ? "English" : "العربية"}</span>
            </button>

            <button
              onClick={() => go("contact")}
              className="bg-primary-600 text-white px-6 py-3 rounded-xl shadow-button hover:shadow-button-hover hover:bg-primary-700 transition-all duration-300 transform hover:-translate-y-0.5 font-semibold text-sm"
            >
              {t("CONTACT US", "تواصل معنا")}
            </button>
          </div>

          {/* Mobile: Language + Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all duration-300 border ${
                isAtTop
                  ? "text-white border-white/30 hover:bg-white/20 bg-white/10 backdrop-blur-sm"
                  : "text-gray-700 border-gray-300 hover:border-primary-500 hover:text-primary-600 bg-white/60"
              }`}
            >
              <i className="ph ph-globe text-sm" />
              <span>{isAr ? "EN" : "عر"}</span>
            </button>
            <button
              className="p-3 rounded-xl transition-all duration-300"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i
                className={`text-xl transition-transform duration-300 ${mobileOpen ? "ph ph-x rotate-90" : "ph ph-list"}`}
                style={{ color: isAtTop ? "white" : "#1E293B" }}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 top-[72px] bg-white z-40 p-4 flex flex-col gap-3 text-base font-bold text-neutral-800 overflow-y-auto pb-24 shadow-2xl">
          {["home", "services", "projects", "about"].map((v) => {
            if (v === "services") {
              return (
                <div key={v} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); go(v); }}
                      className="hover:text-primary-600 transition-colors flex-1"
                    >
                      {t("Services", "الخدمات")}
                    </a>
                    <button
                      onClick={(e) => { e.preventDefault(); setMobileServicesOpen(!mobileServicesOpen); }}
                      className="p-2 text-neutral-500 hover:text-primary-600 bg-neutral-50 rounded-lg"
                    >
                      <i className={`ph ph-caret-down transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {mobileServicesOpen && (
                    <div className={`flex flex-col gap-2 ${isAr ? "pr-4 border-r-2 border-primary-100" : "pl-4 border-l-2 border-primary-100"}`}>
                      {Object.entries(servicesData).map(([key, service]) => (
                        <button
                          key={key}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("service-detail", key);
                            setMobileOpen(false);
                            setMobileServicesOpen(false);
                          }}
                          className="text-left text-sm font-semibold text-neutral-600 hover:text-primary-600 flex items-center gap-3 py-2"
                        >
                          <i className={`ph ${service.icon} text-lg text-primary-500`} />
                          {service.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <a
                key={v}
                href="#"
                onClick={(e) => { e.preventDefault(); go(v); }}
                className="hover:text-primary-600 transition-colors py-2"
              >
                {v === "home"
                  ? t("Home", "الرئيسية")
                  : v === "about"
                  ? t("About Us", "من نحن")
                  : t("Projects", "المشاريع")}
              </a>
            );
          })}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); go("contact"); }}
            className="text-primary-600 py-2"
          >
            {t("Contact Us", "تواصل معنا")}
          </a>
        </div>
      )}
    </>
  );
}
